import { useCallback, useEffect, useState } from "react";
import { Check, X, Trash2, Eye, Phone, Mail, CheckCheck, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { StatusBadge, ActionBtn } from "./Bits";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";

const FILTERS = ["all", "pending", "confirmed", "completed", "cancelled"];

export const BookingsPanel = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/bookings");
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/bookings/${id}`, { status });
      setItems((xs) => xs.map((x) => (x.id === id ? data : x)));
      toast.success(data?.client_emailed ? "Booking confirmed — confirmation email sent to client" : `Booking ${status}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this booking permanently?")) return;
    try {
      await api.delete(`/admin/bookings/${id}`);
      setItems((xs) => xs.filter((x) => x.id !== id));
      setView(null);
      toast.success("Booking deleted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const count = (s) => (s === "all" ? items.length : items.filter((x) => x.status === s).length);
  const shown = filter === "all" ? items : items.filter((x) => x.status === filter);

  return (
    <div data-testid="bookings-panel">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {["pending", "confirmed", "completed", "cancelled"].map((s) => (
          <div key={s} data-testid={`stat-${s}`} className="border border-[#D4AF37]/15 bg-[#0E081D] p-5">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-400">{s}</p>
            <p className="font-display mt-2 text-4xl text-white">{count(s)}</p>
          </div>
        ))}
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
            className={`rounded-full border px-4 py-1.5 text-sm capitalize transition-colors ${filter === f ? "border-[#D4AF37] bg-[#D4AF37] text-[#07040D]" : "border-[#D4AF37]/25 text-slate-300 hover:border-[#D4AF37]/60"}`}>
            {f} <span className="opacity-70">({count(f)})</span>
          </button>
        ))}
        <button onClick={load} data-testid="bookings-refresh" aria-label="Refresh" className="ml-auto flex h-9 w-9 items-center justify-center rounded-full border border-[#D4AF37]/25 text-slate-300 hover:text-white">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      <div className="mt-6 space-y-3" data-testid="bookings-list">
        {!loading && shown.length === 0 && (
          <p data-testid="bookings-empty" className="border border-dashed border-[#D4AF37]/20 p-10 text-center text-slate-400">No bookings here yet.</p>
        )}
        {shown.map((b) => (
          <div key={b.id} data-testid={`booking-row-${b.id}`} className="grid gap-4 border border-[#D4AF37]/15 bg-[#0E081D] p-5 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-3">
              <p className="font-medium text-white">{b.name} {b.source === "assistant" && <span data-testid={`booking-source-${b.id}`} className="ml-1 rounded-full border border-[#D4AF37]/40 px-2 py-0.5 align-middle text-[10px] uppercase tracking-wider text-[#F3E5AB]">via AI assistant</span>}</p>
              <a href={`tel:${b.phone}`} className="mt-1 flex items-center gap-1.5 text-sm text-slate-300 hover:text-white"><Phone className="h-3.5 w-3.5" />{b.phone}</a>
              {b.email && <a href={`mailto:${b.email}`} className="flex items-center gap-1.5 break-all text-sm text-slate-300 hover:text-white"><Mail className="h-3.5 w-3.5 shrink-0" />{b.email}</a>}
            </div>
            <div className="lg:col-span-3">
              <p className="text-sm text-[#F3E5AB]">{b.service}</p>
              <p className="text-sm text-slate-300">{b.date} · {b.time}</p>
            </div>
            <div className="lg:col-span-2"><StatusBadge status={b.status} testId={`booking-status-${b.id}`} /></div>
            <div className="flex flex-wrap gap-2 lg:col-span-4 lg:justify-end">
              <ActionBtn testId={`booking-view-${b.id}`} onClick={() => setView(b)}><Eye className="h-3.5 w-3.5" />View</ActionBtn>
              {b.status !== "confirmed" && <ActionBtn tone="gold" testId={`booking-confirm-${b.id}`} onClick={() => setStatus(b.id, "confirmed")}><Check className="h-3.5 w-3.5" />Confirm</ActionBtn>}
              {b.status === "confirmed" && <ActionBtn testId={`booking-complete-${b.id}`} onClick={() => setStatus(b.id, "completed")}><CheckCheck className="h-3.5 w-3.5" />Complete</ActionBtn>}
              {b.status !== "cancelled" && <ActionBtn testId={`booking-cancel-${b.id}`} onClick={() => setStatus(b.id, "cancelled")}><X className="h-3.5 w-3.5" />Cancel</ActionBtn>}
              <ActionBtn tone="danger" testId={`booking-delete-${b.id}`} onClick={() => remove(b.id)}><Trash2 className="h-3.5 w-3.5" /></ActionBtn>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent data-testid="booking-detail-dialog" className="border-[#D4AF37]/30 bg-[#0E081D] text-slate-100">
          {view && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-3xl text-white">{view.name}</DialogTitle>
                <DialogDescription className="text-slate-400">Requested {new Date(view.created_at).toLocaleString()}</DialogDescription>
              </DialogHeader>
              <dl className="grid grid-cols-3 gap-y-3 text-sm">
                {[["Service", view.service], ["Date", view.date], ["Time", view.time], ["Phone", view.phone], ["Email", view.email || "—"], ["Source", view.source === "assistant" ? "AI assistant" : "Website form"], ["Status", view.status]].map(([k, v]) => (
                  <div key={k} className="contents"><dt className="text-slate-400">{k}</dt><dd className="col-span-2 break-all capitalize-first">{v}</dd></div>
                ))}
              </dl>
              <div className="border-t border-[#D4AF37]/15 pt-4">
                <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Message</p>
                <p data-testid="booking-detail-message" className="mt-2 whitespace-pre-wrap text-sm text-slate-200">{view.message || "—"}</p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
